import type { ViewFieldsFragment } from '@salescore/client-api'
import { POSTHOG_EVENTS, posthogTrackClick } from '@salescore/client-base'
import { useCanForView } from '@salescore/client-common'
import { generateLabelSqlFieldName } from '@salescore/core'
import { EChartPie } from '@salescore/frontend-common'
import { Empty } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import type { ReactElement } from 'react'

import type { KpiPivotConfig } from '../../../recoil/context'
import { useNavigationModal } from '../../../recoil/navigation/hooks'
import type { ChartDesignSettings, KpiPieChartCategoryFilters } from '../ViewUIKpi'
import { ChartCardWrapper, useRecords } from './KpiStackedChart'

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function KpiPieChart({
  pivot,
  kpiPieChartCategoryFilters,
  chartDesignSettings: {
    chartValueLabelDisplayMode,
    chartValueLabelUnitType,
    chartValueLabelDecimalPlaceType,
    chartLegendsDisplayMode,
  },
  view,
}: {
  pivot: KpiPivotConfig
  kpiPieChartCategoryFilters: KpiPieChartCategoryFilters
  chartDesignSettings: ChartDesignSettings
  view: ViewFieldsFragment
}): ReactElement {
  const { records, key1, key2, csv } = useRecords({ pivot, view })
  const { drillDownModal } = useNavigationModal()
  const canDownloadCsv = useCanForView('download-csv', view)

  if (pivot.rows.length === 0) {
    return <Empty description={t(`軸を指定してください`)} />
  }

  if (pivot.rows.length > 2) {
    return <Empty description={t(`グラフを描画するには、軸を1つ以上2つ以下にしてください`)} />
  }

  return (
    <ChartCardWrapper>
      <EChartPie
        title={view.name}
        onSeriesClick={(value) => {
          drillDownModal.showModal({
            type: 'kpi',
            dimensionValues: {
              rows: [
                {
                  value: value.key,
                  label: value.label,
                },
                value.groupKey !== undefined && value.groupKey !== ''
                  ? {
                      value: value.groupKey,
                      label: value.groupLabel,
                    }
                  : undefined,
              ].filter(isNotNullish),
              columns: [],
            },
          })
        }}
        records={records}
        ratioKey={key1}
        groupKey={key2}
        ratioLabelKey={generateLabelSqlFieldName(key1)}
        groupLabelKey={generateLabelSqlFieldName(key2)}
        groupFilters={kpiPieChartCategoryFilters.map((x) => x.key)}
        valueLabelDisplayMode={chartValueLabelDisplayMode}
        valueLabelUnitType={chartValueLabelUnitType}
        valueLabelDecimalPlaceType={chartValueLabelDecimalPlaceType}
        legendDisplayMode={chartLegendsDisplayMode}
        valueKey={JSON.stringify(['actual'])}
        csv={csv}
        disableCsvDownload={!canDownloadCsv}
        onCsvDownloadClick={() => {
          posthogTrackClick(POSTHOG_EVENTS.click_kpi_pie_chart_csv_download, view.id)
        }}
        onImageDownloadClick={() => {
          posthogTrackClick(POSTHOG_EVENTS.click_kpi_pie_chart_image_download, view.id)
        }}
      />
    </ChartCardWrapper>
  )
}
